import React, { useEffect, useState, useRef, useMemo } from "react";
import { LuPanelLeftOpen, LuPanelRightOpen } from "react-icons/lu";
import useTitanApp from "../../../../../hooks/useTitanApp";
import { RiWalkFill } from "react-icons/ri";
import { RxCaretDown, RxCaretUp } from "react-icons/rx";
import { motion } from "framer-motion";
import { FiArrowRight } from "react-icons/fi";
import { IoIosCheckmarkCircle, IoIosSearch } from "react-icons/io";
import { CiCirclePlus } from "react-icons/ci";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  setIsDemDXTemplate,
  setOpenAddTemplate,
  setSecondPatientPanelTitle,
  setTogglePatientPanelTwo,
  setCurrentShowEditAiTemplateModal,
  setDemDxSummary,
  setIsDemDxActive,
  setPatientSummary,
  setDemDXForChat,
  setPatientTranscription,
  addConsultationTemplate,
  updateConsultationTemplate,
  setTranscript,
  setDemDxSummaryText,
  setTogglePatientPanelOne,
  setFirstPatientPanelTitle,
  getPatientConsultationByConsultationId,
  getPatientDocumentById,
  setViewOpen,
  setSnomedCodes,
} from "../../../../../dispatchers/patientDispatchers";
import { setViewingDoc } from "@/dispatchers/documentDispatchers";
import { Skeleton } from "@mui/material";
import { genderTypes } from "../../../../../enum/genderTypes";
import {
  getAiTemplateById,
  setAiqa,
  setHydratedTemplateSections,
  setIsTemplateSelected,
  setSelectedTemplate,
  setStreamedText,
} from "../../../../../dispatchers/templateDispatchers";
import { FiEdit2 } from "react-icons/fi";
import PatientDemDxIframe from "./PatientDemDxIframe"; // adjust the path as needed
import config from "../../../../../config.js";
import { v4 as uuidv4 } from "uuid";
import { ArrowRight, FileText, Plus } from "lucide-react";
import NotesPanel from "../../NotesPanel";
import { ConsultationButtonStatus } from "../../../../../enum/ConsultationButtonStatus.js";
import ErrorConsultationModal from "../../ErrorConsultationModal.tsx";
import { setConsultationLoadError } from "../../../../../dispatchers/appDispatchers.js";
import { useDocumentsData } from "../../../../Documents/documentsData.ts";
import { NHSServicesForms } from "../../../../../components/nhs-templates/NHSServicesForms.jsx";
import { useToast } from "@/hooks/use-toast";
import {
  nhs111ServiceId,
  nhs111TemplateId,
  nhsContraceptionTemplateId,
  nhsSinusitisTemplateId,
  nhsSoreThroatTemplateId,
  nhsImpetigoTemplateId,
  nhsInsectBitesTemplateId,
  nhsShinglesTemplateId,
  nhsUtiTemplateId,
  nhsAomTemplateId,
  nhsHypertensionStage1TemplateId,
  nhsHypertensionABPMFittingTemplateId,
  nhsHypertensionABPMFollowupTemplateId,
  nhsAhtWeightTemplateId,
} from "@/data/constantStrings";
import { Badge } from "@/components/ui/badge";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseTemplateUpsert } from "@/data/constantStrings";
import { getCompanyDocumentsByCompanyId } from "@/dispatchers/companyDocumentsDispatchers";
import { usePatientVitalsState } from "@/lib/dashboard/vitalsData";
import { getCompanyMarketplaceApps } from "@/dispatchers/marketAppDispacher";
import {
  isNhsService,
  getNhsServiceName,
} from "@/components/nhs-templates/NHSServicesHelper";

export default function PatientTemplate({
  stopTranscription,
  isDemDXGlobalCheckRef,
}) {
  const { dexDxUrl, demDxIssuer, license, demDxToken } = config;
  const { appState, templateState, patientState, pharmacyServiceState, marketAppState } =
    useTitanApp();
  const { showNHS111ReferralTab, allPharmacies } = pharmacyServiceState || {};

  const canUpsertTemplate = checkUserPermissions(
    accessTitanverseTemplateUpsert,
  );
  const { darkMode, consultationLoadError, features, companyId, currentPharmacyId } =
    appState || {};
  const {
    isDropdownAiTemplatesLoading,
    currentAiTemplates,
    isSelectedAiTemplateLoading,
    selectedTemplate,
    aiqaData,
    hydratedTemplateSections,
    aiTemplateLoadError,
  } = templateState || {};
  const {
    demDXTemplates,
    selectedPatientConsultation,
    isConsultationEnabled,
    selectedPatientDetails,
    isDemDXTemplate,
    demDxSummary,
    consultationId,
    isCallStarted,
    consultationMediaButtonMode,
    isSummaryGenerated,
    hasPreConsultTab,
  } = patientState || {};

  const [templateDropdown, setTemplateDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [demDxForceReload, setDemDxForceReload] = useState(0);
  const { companyDocuments, fetchDocumentById } = useDocumentsData();
  const documents = companyDocuments;
  const [isNhsSelected, setIsNhsSelected] = useState(
    () =>
      isNhsService(selectedPatientConsultation?.templateId) ||
      selectedPatientConsultation?.template?.templateSections?.some(
        (s: any) => s.sectionTitle === "__pathway_engine_metadata__",
      ) === true,
  );

  const { ifNHS111ServiceEnabled, ifPharmacyFirstEnabled, ifAHTWeightManagementEnabled } = features;

  const { companyMarketplaceAppsData } = marketAppState || {};

  useEffect(() => {
    if (companyId) {
      getCompanyMarketplaceApps(companyId);
    }
  }, [companyId]);

  const currentPharmacyOds = useMemo(() => {
    const matched = allPharmacies?.find(
      (p: any) => (p?.teamPharmacyID || p?.pharmacyID) === currentPharmacyId,
    );
    return matched?.odsCode || matched?.ODSCode || matched?.ods || "";
  }, [allPharmacies, currentPharmacyId]);

  const isAhtWeightManagementInstalled = useMemo(
    () => companyMarketplaceAppsData?.some(
      (app: any) => app.marketType === 5 && app.ods === currentPharmacyOds,
    ),
    [companyMarketplaceAppsData, currentPharmacyOds]
  );

  const isMalePatient =
    genderTypes[selectedPatientDetails?.sex]?.toLowerCase() === "male";

  const nhsTemplates = [
    {
      _id: nhs111TemplateId,
      templateName: "NHS 111 Emergency Supply",
      demdx_Type: null,
    },
    {
      _id: nhsContraceptionTemplateId,
      templateName: "NHS Contraception",
      demdx_Type: null,
    },
    {
      _id: nhsHypertensionStage1TemplateId,
      templateName: "NHS Hypertension - Stage 1: Initial Consultation",
      demdx_Type: null,
    },
    {
      _id: nhsHypertensionABPMFittingTemplateId,
      templateName: "NHS Hypertension - Stage 2: ABPM Fitting",
      demdx_Type: null,
    },
    {
      _id: nhsHypertensionABPMFollowupTemplateId,
      templateName: "NHS Hypertension - Stage 3: ABPM Follow Up",
      demdx_Type: null,
    },
  ];

  const pharmacyFirstTemplates = [
    {
      _id: nhsSinusitisTemplateId,
      templateName: "NHS Pharmacy First Acute Sinusitis",
      demdx_Type: null,
    },
    {
      _id: nhsSoreThroatTemplateId,
      templateName: "NHS Pharmacy First Acute Sore Throat",
      demdx_Type: null,
    },
    {
      _id: nhsImpetigoTemplateId,
      templateName: "NHS Pharmacy First Impetigo",
      demdx_Type: null,
    },
    {
      _id: nhsInsectBitesTemplateId,
      templateName: "NHS Pharmacy First Infected Insect Bites",
      demdx_Type: null,
    },
    {
      _id: nhsShinglesTemplateId,
      templateName: "NHS Pharmacy First Shingles",
      demdx_Type: null,
    },
    {
      _id: nhsUtiTemplateId,
      templateName: "NHS Pharmacy First Uncomplicated UTI",
      demdx_Type: null,
    },
    {
      _id: nhsAomTemplateId,
      templateName: "NHS Pharmacy First Acute Otitis Media",
      demdx_Type: null,
    },
  ];

  const pgdTemplates = [
    {
      _id: nhsAhtWeightTemplateId,
      templateName: "Weight Management (AHT)",
      demdx_Type: null,
    },
  ];

  // existing filteredTemplates definition — add NHS group
  const filteredTemplates =
    selectedPatientConsultation?.serviceID === nhs111ServiceId &&
    selectedPatientConsultation.serviceType === 1
      ? []
      : [
          {
            id: 1,
            title: "Personal Templates",
            templates: currentAiTemplates
              ?.filter((template) =>
                template?.templateName
                  ?.toLowerCase()
                  .includes(searchTerm.toLowerCase()),
              )
              ?.sort((a, b) =>
                a?.templateName
                  ?.toLowerCase()
                  .localeCompare(b?.templateName?.toLowerCase()),
              ),
          },
          {
            id: 2,
            title: "DemDx (Presenting Complaint) Templates",
            isDemDX: true,
            withTags: true,
            templates: [
              ...(demDXTemplates?.predictions?.list_Minor_Illness || []),
              ...(demDXTemplates?.predictions?.list_Pharmacy_First || []),
              ...(demDXTemplates?.predictions?.list_Structured || []),
            ]
              .map((item) => ({
                name: item.name,
                demdx_Type: item.demdx_Type,
                tag_Label: item.tag_Label,
              }))
              .filter((item) =>
                item.name?.toLowerCase().includes(searchTerm.toLowerCase()),
              ),
          },
          {
            id: 3,
            title: "DemDx (Direct Condition) Templates",
            isDemDX: true,
            templates: [...(demDXTemplates?.predictions?.list_Endpoints || [])]
              .map((item) => ({
                name: item.name,
                demdx_Type: item.demdx_Type,
                tag_Label: item.tag_Label,
              }))
              .filter((item) =>
                item.name?.toLowerCase().includes(searchTerm.toLowerCase()),
              ),
          },

          ...(ifNHS111ServiceEnabled
            ? [
                {
                  id: 4,
                  title: "NHS",
                  templates: nhsTemplates
                    .filter(
                      (template) =>
                        !isMalePatient ||
                        template._id !== nhsContraceptionTemplateId,
                    )
                    .filter((template) =>
                      (template.templateName || "")
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()),
                    ),
                },
              ]
            : []),
          ...(ifPharmacyFirstEnabled
            ? [
                {
                  id: 6,
                  title: "Pharmacy First",
                  templates: pharmacyFirstTemplates.filter((template) =>
                    (template.templateName || "")
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()),
                  ),
                },
              ]
            : []),
          ...(ifAHTWeightManagementEnabled && isAhtWeightManagementInstalled
            ? [
                {
                  id: 5,
                  title: "PGD",
                  templates: pgdTemplates.filter((template) =>
                    (template.templateName || "")
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()),
                  ),
                },
              ]
            : []),
        ];
  const handleAnswerChange = (sectionIdx, controlIdx, value) => {
    let currentTemplate = hydratedTemplateSections;
    if (!currentTemplate) {
      const templateSectionsToUse =
        selectedTemplate?.templateSections ??
        selectedPatientConsultation?.template?.templateSections;

      if (!templateSectionsToUse) return;

      currentTemplate = {
        templateSections: templateSectionsToUse.map((section) => ({
          ...section,
          userControls: section.userControls?.map((control) => ({
            ...control,
            value: control.value || "",
          })),
        })),
      };
    }

    const updatedSections = currentTemplate.templateSections.map(
      (section, sIdx) => {
        if (sIdx !== sectionIdx) return section;
        return {
          ...section,
          userControls: section.userControls.map((control, cIdx) =>
            cIdx === controlIdx ? { ...control, value } : control,
          ),
        };
      },
    );

    const updatedTemplate = {
      ...currentTemplate,
      templateSections: updatedSections,
    };

    setHydratedTemplateSections(updatedTemplate);

    if (
      consultationMediaButtonMode === "manual" ||
      selectedPatientConsultation?.consultationButtonStatus ===
        ConsultationButtonStatus.Manual
    ) {
      generateTranscription(updatedTemplate);
    }
  };

  function generateTranscription(hydratedTemplateSections) {
    if (
      !hydratedTemplateSections ||
      !Array.isArray(hydratedTemplateSections?.templateSections)
    ) {
      return "";
    }
    let transcription = "";
    hydratedTemplateSections?.templateSections.forEach((section) => {
      if (Array.isArray(section?.userControls)) {
        section?.userControls?.forEach((control) => {
          if (control.label && control.value) {
            transcription += ` ${control.label} ${control.value} `;
          }
        });
      }
    });
    setTranscript(transcription.trim());
    setPatientTranscription(transcription.trim());
    return transcription.trim();
  }

  const hasInitializedRef = useRef(false);

  useEffect(() => {
    const activeConsultation = selectedPatientConsultation?.template;
    const templateSectionsToUse =
      selectedTemplate?.templateSections ??
      activeConsultation?.templateSections;

    if (!templateSectionsToUse || !Array.isArray(templateSectionsToUse)) return;

    if (templateSectionsToUse.some((s) => s.sectionTitle === "__pathway_engine_metadata__")) return;

    const isAiqaEmpty =
      !aiqaData ||
      (Array.isArray(aiqaData) && aiqaData.length === 0) ||
      (typeof aiqaData === "object" && Object.keys(aiqaData).length === 0);

    if (hasInitializedRef.current && isAiqaEmpty) {
      return;
    }

    if (isAiqaEmpty && isSummaryGenerated) {
      return;
    }

    let globalIndex = 0;

    const parsedAnswers = aiqaData ? aiqaData : {};

    const hydratedSections = templateSectionsToUse.map((section, sIdx) => {
      const existingSection =
        hydratedTemplateSections?.templateSections?.[sIdx];

      const hydratedControls = section?.userControls?.map((control, index) => {
        const existingControl = existingSection?.userControls?.[index];
        const existingValue = existingControl?.value;

        const answerKey = `a${globalIndex + 1}`;
        const aiAnswer =
          parsedAnswers[answerKey] &&
          parsedAnswers[answerKey] !== "Not specified"
            ? parsedAnswers[answerKey]
            : null;
        const answer = aiAnswer || existingValue || control.value;

        globalIndex++;
        return {
          ...control,
          value: answer,
        };
      });
      return {
        ...section,
        userControls: hydratedControls,
      };
    });

    const fullHydratedTemplate = {
      ...selectedPatientConsultation,
      templateSections: hydratedSections,
    };

    if (isCallStarted) {
      (async () => {
        try {
          await updateConsultationTemplate({
            consultationId,
            payload: hydratedSections,
          });
        } catch (error) {
          console.error("Error updating consultation template:", error);
          setConsultationLoadError(true);
        }
      })();
    }

    setHydratedTemplateSections(fullHydratedTemplate);
    if (selectedTemplate?.template) {
      setSelectedTemplate(selectedTemplate?.template);
    }

    hasInitializedRef.current = true;
  }, [aiqaData]);

  useEffect(() => {
    hasInitializedRef.current = false;
  }, [selectedTemplate?._id, consultationId]);
  const handleAddTemplates = () => {
    setOpenAddTemplate(true);
  };

  let globalIndex = 0;

  const handleSecondPatientPanelCollapsing = () => {
    setTogglePatientPanelTwo(true);
    setSecondPatientPanelTitle("Template");
  };

  useEffect(() => {
    if (isNhsService(selectedPatientConsultation?.templateId)) {
      setIsNhsSelected(true);
      setPatientSummary(null);
      setDemDxSummary(null);
      setStreamedText("");
      setSnomedCodes([]);
      setDemDxSummaryText("");
      setTemplateDropdown(false);
      setIsTemplateSelected(true);
    } else {
      setIsNhsSelected(false);
    }
  }, [selectedPatientConsultation?.templateId]);

  const handleTemplateSelection = async (e, template, isDemDxGroup) => {
    try {
      setIsNhsSelected(isNhsService(template._id));
      e.stopPropagation();
      setPatientSummary(null);
      setDemDxSummary(null);
      setStreamedText("");
      setSnomedCodes([]);
      setDemDxSummaryText("");
      setTemplateDropdown(false);

      const payloadTemplate = isDemDxGroup
        ? { ...template, templateName: template.name }
        : template;

      if (isDemDxGroup) {
        delete payloadTemplate.name;
      }

      await addConsultationTemplate({
        consultationId: consultationId,
        template: payloadTemplate,
      });
      setSelectedTemplate(template);
      setIsTemplateSelected(true);
      setPatientTranscription("");
      setHydratedTemplateSections(null);
      setAiqa([]);

      if (template?.linkedDocumentId && companyId) {
        await getCompanyDocumentsByCompanyId(companyId);
      }

      if (isDemDxGroup) {
        setTogglePatientPanelOne(true);
        setFirstPatientPanelTitle("Profile");
        isDemDXGlobalCheckRef.current = true;
        setIsDemDxActive(true);
        setDemDXForChat(true);

        const patientId = selectedPatientDetails?.patientID;
        const patientBirthdate = selectedPatientDetails?.dob
          ? new Date(selectedPatientDetails.dob).toISOString().split("T")[0]
          : "";
        const patientGender = genderTypes[selectedPatientDetails?.sex];
        const patientName = selectedPatientDetails?.patientName;

        const isDemDxBracketType =
          template.demdx_Type === "minor_illness" ||
          template.demdx_Type === "pharmacy_first" ||
          template.demdx_Type === "structured_pathway";
        const demDxKey = localStorage.getItem("demDexToken");
        const demDxPayload = {
          assessmentId: consultationId || "",
          demdx_name: isDemDxBracketType
            ? `[${
                template.demdx_Type === "minor_illness"
                  ? "Minor Illness"
                  : template.demdx_Type === "pharmacy_first"
                    ? "Pharmacy First"
                    : "Structured Pathway"
              }] ${template.name || ""}`
            : template.name || "",
          demdx_type: template.demdx_Type,
          licenseKey: demDxKey,
          subject: {
            id: patientId,
            birthdate: patientBirthdate,
            gender: patientGender,
            name: patientName,
          },
          token: demDxToken,
        };

        setIsDemDXTemplate(demDxPayload);
        setDemDxForceReload((prev) => prev + 1); // Force reload
      } else {
        setIsDemDxActive(false);
        setIsDemDXTemplate(null);
        if (isConsultationEnabled) return;

        setSearchTerm("");
        setAiqa([]);
        await getAiTemplateById(template._id);
      }
      await getPatientConsultationByConsultationId(consultationId);
    } catch (error) {
      console.error("Error in handleTemplateSelection:", error);
      setConsultationLoadError(true);
    }
  };
  const templateSectionsToRenderRaw =
    Array.isArray(hydratedTemplateSections?.templateSections) &&
    hydratedTemplateSections.templateSections.length > 0
      ? hydratedTemplateSections.templateSections
      : Array.isArray(
            selectedPatientConsultation?.template?.templateSections,
          ) &&
          selectedPatientConsultation?.template?.templateSections.length > 0
        ? selectedPatientConsultation?.template?.templateSections
        : selectedTemplate?.templateSections &&
            Array.isArray(selectedTemplate?.templateSections) &&
            selectedTemplate?.templateSections.length > 0
          ? selectedTemplate?.templateSections
          : null;

  const isPathwaySavedState = templateSectionsToRenderRaw?.some(
    (s) => s.sectionTitle === "__pathway_engine_metadata__",
  );
  const templateSectionsToRender = isPathwaySavedState
    ? null
    : templateSectionsToRenderRaw;

  const templateNameToShow = isNhsSelected
    ? getNhsServiceName(selectedPatientConsultation?.templateId) ||
      selectedTemplate?.templateName ||
      selectedPatientConsultation?.template?.templateName ||
      selectedTemplate?.name ||
      "Choose a template"
    : selectedTemplate?.templateName ||
      selectedPatientConsultation?.template?.templateName ||
      selectedTemplate?.name ||
      "Choose a template";

  const isManualMode =
    consultationMediaButtonMode !== "transcription" &&
    (consultationMediaButtonMode === "manual" ||
      selectedPatientConsultation?.consultationButtonStatus ===
        ConsultationButtonStatus.Manual);
  const isTemplateDisabled =
    selectedPatientConsultation?.consultationStatus === 1;

  const linkedDocument =
    selectedTemplate?.linkedDocumentId &&
    documents?.find((doc) => doc._id === selectedTemplate.linkedDocumentId);

  const handleViewDocument = async (doc) => {
    try {
      const documentId = doc?._id || doc?.documentId;

      if (!documentId) {
        console.warn("No document ID found in document data", doc);

        return;
      }

      let result;
      if (doc?._id) {
        result = await fetchDocumentById(
          documentId,
          doc?.mainFile?.fileName,
          1,
        );
      } else {
        result = await getPatientDocumentById(documentId);
      }

      const documentContent = result?.base64Content;
      if (!documentContent) {
        console.warn("No document content received");

        return;
      }

      let fileExtension = result?.fileExtension || result?.fileExtenstion || "";
      const fileName =
        result?.fileName || doc?.mainFile?.fileName || doc?.title || "Document";

      if (fileExtension && !fileExtension.startsWith(".")) {
        fileExtension = "." + fileExtension;
      }

      const mimeMap = {
        ".pdf": "application/pdf",
        ".docx":
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ".doc": "application/msword",
        ".png": "image/png",
        ".jpg": "image/jpeg",
        ".jpeg": "image/jpeg",
        ".gif": "image/gif",
        ".webp": "image/webp",
        ".txt": "text/plain",
        ".xlsx":
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ".pptx":
          "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      };

      const mimeType =
        mimeMap[fileExtension?.toLowerCase()] ||
        result?.mimeType ||
        "application/octet-stream";

      // Handle base64 content
      let blobUrl;

      if (documentContent.startsWith("data:")) {
        // Already has data URI prefix, use directly
        blobUrl = documentContent;
      } else {
        // Convert base64 to blob
        const byteCharacters = atob(documentContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        blobUrl = URL.createObjectURL(blob);
      }

      setViewingDoc({
        title: fileName,
        url: blobUrl,
        mimeType,
      });
      setViewOpen(true);
    } catch (err) {
      console.error("Error loading document:", err);
    }
  };
  const showPreConsult = hasPreConsultTab;

  const isTemplateOverdue = (template) => {
    if (!template.lastReviewedAt) return true;

    const lastReviewDate = new Date(template.lastReviewed);
    const currentDate = new Date();

    // Calculate the date 12 months ago from the current date
    const twelveMonthsAgo = new Date(
      currentDate.getFullYear() - 1,
      currentDate.getMonth(),
      currentDate.getDate(),
    );

    // If the last review date is before the date 12 months ago, it's overdue.
    return lastReviewDate < twelveMonthsAgo;
  };

  return (
    <div className="pc-tabbed-content patient-template-wrapper">
      <div className="section-title">
        <div className="name-collapsible">
          <LuPanelRightOpen
            className="collapse-icon"
            color={darkMode ? "#fff" : "#0a0a0a"}
            onClick={handleSecondPatientPanelCollapsing}
          />
          <h5 className="font-medium">Patient Consultation</h5>
          <Badge variant="secondary" className="text-xs">
            <RiWalkFill className="mr-1" /> Walk in
          </Badge>
        </div>
      </div>

      <motion.div
        className="template-dropdown"
        onClick={(e) => {
          if (isTemplateDisabled) {
            e.stopPropagation();
            return;
          }
          if (
            selectedPatientConsultation?.serviceID === nhs111ServiceId &&
            selectedPatientConsultation.serviceType === 1
          )
            return;
          if (
            e.target.closest(".template-list-box") ||
            e.target.closest(".search-box") ||
            e.target.closest(".template-item")
          ) {
            e.stopPropagation();
            return;
          }

          e.stopPropagation();
          if (
            !isConsultationEnabled &&
            (selectedPatientConsultation?.consultationButtonStatus === 0 ||
              selectedPatientConsultation?.consultationButtonStatus === 4 ||
              selectedPatientConsultation?.consultationStatus !== 2 ||
              selectedPatientConsultation?.consultationButtonStatus ===
                ConsultationButtonStatus.InProgress)
          ) {
            setTemplateDropdown((prev) => !prev);
          }
        }}
        style={{ border: `1px solid ${darkMode ? "#3c3c3c" : "#f3ebeb"}` }}
        animate={{
          maxHeight: templateDropdown
            ? 320
            : isNhsSelected ||
                selectedTemplate?.templateName ||
                selectedTemplate?.name ||
                selectedPatientConsultation?.template?.templateName
              ? 60
              : 42,
        }}
        transition={{ type: "spring", stiffness: 50 }}
      >
        <div className="initial-container">
          <div
            className="header"
            onClick={async (e) => {
              e.stopPropagation();
              if (isTemplateDisabled) return;
              if (
                selectedPatientConsultation?.serviceID === nhs111ServiceId &&
                selectedPatientConsultation.serviceType === 1
              )
                return;

              if (
                !isConsultationEnabled &&
                (selectedPatientConsultation?.consultationButtonStatus === 0 ||
                  selectedPatientConsultation?.consultationButtonStatus === 4 ||
                  selectedPatientConsultation?.consultationStatus !== 2 ||
                  selectedPatientConsultation?.consultationButtonStatus ===
                    ConsultationButtonStatus.InProgress)
              ) {
                setTemplateDropdown((prev) => !prev);
              }
            }}
          >
            <p>Choose a template</p>
            {templateDropdown ? (
              <RxCaretUp size={22} color={darkMode ? "#fff" : "#0a0a0a"} />
            ) : (
              <RxCaretDown size={22} color={darkMode ? "#fff" : "#0a0a0a"} />
            )}
          </div>

          {templateNameToShow !== "Choose a template" && (
            <div className="placeholder-text">
              <p>{templateNameToShow}</p>
              {selectedTemplate.demdx_Type && (
                <span
                  className={`demdx-tag ${
                    selectedTemplate.demdx_Type === "minor_illness"
                      ? "tag-minor"
                      : selectedTemplate.demdx_Type === "pharmacy_first"
                        ? "tag-pharmacy"
                        : selectedTemplate.demdx_Type === "structured_pathway"
                          ? "tag-structured"
                          : ""
                  }`}
                >
                  {selectedTemplate.tag_Label != null
                    ? selectedTemplate.tag_Label
                    : selectedTemplate.demdx_Type === "minor_illness"
                      ? "Minor Illness"
                      : selectedTemplate.demdx_Type === "pharmacy_first"
                        ? "Pharmacy First"
                        : selectedTemplate.demdx_Type === "structured_pathway"
                          ? "Minor Illness - Structured"
                          : ""}
                </span>
              )}
            </div>
          )}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{
            opacity: templateDropdown ? 1 : 0,
          }}
          transition={{
            ease: "easeOut",
            duration: 0.3,
            delay: templateDropdown ? 0.3 : 0,
          }}
          className="template-list-box"
        >
          {/* Search Bar */}
          <div className="search-box">
            <IoIosSearch size={14} className="icons-color" />
            <input
              type="text"
              placeholder="Search for a template..."
              className={darkMode ? "text-white" : "text-black"}
              value={searchTerm}
              onChange={(e) => {
                e.stopPropagation();
                setSearchTerm(e.target.value);
              }}
              disabled={isConsultationEnabled || isTemplateDisabled}
            />
          </div>

          {/* Templates List */}
          <div className="template-list">
            {isDropdownAiTemplatesLoading ? (
              Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="template-item sk">
                  <Skeleton
                    width={250}
                    height={35}
                    animation="wave"
                    sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                  />
                </div>
              ))
            ) : filteredTemplates?.length > 0 ? (
              filteredTemplates.map((group, groupIndex) => (
                <div key={groupIndex} className="template-group">
                  {group?.id === 1 && canUpsertTemplate && (
                    <div
                      className="flex items-center add-new-template-box"
                      onClick={handleAddTemplates}
                    >
                      <Plus className="plus-icon" />
                      <span className="text-blue-medium">
                        Create New Template with AI
                      </span>
                    </div>
                  )}
                  {/* Dynamic Section Title */}
                  <div className="section-title">
                    <p>{group.title}</p>
                  </div>

                  {/* Templates inside the group */}
                  {group.templates?.length > 0 ? (
                    group.templates.map((template, index) => {
                      const isDemDxGroup = group.withTags === true;
                      const tagText = isDemDxGroup
                        ? template.tag_Label !== null
                          ? template.tag_Label
                          : template.demdx_Type === "minor_illness"
                            ? "Minor Illness"
                            : template.demdx_Type === "pharmacy_first"
                              ? "Pharmacy First"
                              : template.demdx_Type === "structured_pathway"
                                ? "Minor Illness - Structured"
                                : null
                        : null;

                      return (
                        <div
                          key={template._id || index}
                          className="template-item"
                          onClick={(e) => {
                            if (template?.forceUserSignOff === true) {
                              e.stopPropagation();
                              return;
                            }
                            handleTemplateSelection(e, template, group.isDemDX);
                          }}
                        >
                          <FiArrowRight
                            className="arrow"
                            color={darkMode ? "#fff" : "#0a0a0a"}
                          />
                          <p className="flex items-center w-full gap-2 template-name">
                            {template.templateName || template.name}
                            <span className="flex items-center gap-2">
                              {template?.forceUserSignOff === true && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 border border-red-200 text-red-800 text-xs font-medium">
                                  Not Compliant
                                </span>
                              )}

                              {group?.id === 1 &&
                                template?.linkedDocumentId &&
                                isTemplateOverdue(template) && (
                                  <Badge
                                    variant="destructive"
                                    className="text-xs px-1.5 py-0.5 bg-red-100 text-red-800 border-red-200"
                                  >
                                    Review Due
                                  </Badge>
                                )}

                              {tagText && (
                                <span
                                  className={`demdx-tag ${
                                    template.demdx_Type === "minor_illness"
                                      ? "tag-minor"
                                      : template.demdx_Type === "pharmacy_first"
                                        ? "tag-pharmacy"
                                        : template.demdx_Type ===
                                            "structured_pathway"
                                          ? "tag-structured"
                                          : ""
                                  }`}
                                >
                                  {tagText}
                                </span>
                              )}
                            </span>

                            {group?.id === 1 && canUpsertTemplate && (
                              <FiEdit2
                                className="ml-auto"
                                color={darkMode ? "#fff" : "#000"}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCurrentShowEditAiTemplateModal(
                                    true,
                                    template,
                                    templateNameToShow,
                                  );
                                }}
                              />
                            )}
                          </p>
                        </div>
                      );
                    })
                  ) : (
                    <p className="no-templates-dd">
                      No templates found in this group
                    </p>
                  )}
                </div>
              ))
            ) : (
              <p className="no-templates-dd">No templates found</p>
            )}
          </div>
        </motion.div>
      </motion.div>
      {linkedDocument && (
        <div className="mt-2">
          <h4
            className={`text-xs font-medium mb-1 ${
              darkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Linked Document
          </h4>

          <button
            onClick={() => handleViewDocument(linkedDocument)}
            className={`w-full flex items-center gap-1.5 p-2 rounded transition-all text-left ${
              darkMode
                ? "bg-gray-800/50 hover:bg-gray-700/70"
                : "bg-white hover:bg-gray-50 border border-gray-200"
            }`}
          >
            <FileText
              className={`w-3.5 h-3.5 ${
                darkMode ? "text-blue-400" : "text-blue-600"
              }`}
            />

            <div className="flex-1 min-w-0">
              <p
                className={`text-xs font-medium truncate ${
                  darkMode ? "text-gray-200" : "text-gray-800"
                }`}
              >
                {linkedDocument.title}
              </p>
              <p
                className={`text-xs ${
                  darkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                {linkedDocument.type}
              </p>
            </div>

            <ArrowRight
              className={`w-3.5 h-3.5 ${
                darkMode ? "text-gray-500" : "text-gray-800"
              }`}
            />
          </button>
        </div>
      )}

      {isDemDXTemplate ? (
        <div className="iframe-container">
          {isDemDXTemplate && (
            <PatientDemDxIframe
              key={`demdx-${demDxForceReload}`}
              isDemDXTemplate={isDemDXTemplate}
              stopTranscription={stopTranscription}
              ConsultationId={consultationId}
            />
          )}
        </div>
      ) : (
        <div className="template-list-content">
          {isSelectedAiTemplateLoading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="template-content-list-item sk">
                <Skeleton
                  width={150}
                  height={35}
                  animation="wave"
                  sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                />
                <Skeleton
                  width={400}
                  height={35}
                  animation="wave"
                  sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                />
                <Skeleton
                  width={400}
                  height={35}
                  animation="wave"
                  sx={{ bgcolor: darkMode ? "grey.900" : "" }}
                />
              </div>
            ))
          ) : templateSectionsToRender && !isNhsSelected ? (
            templateSectionsToRender?.map((templateSection, sectionIdx) => (
              <div
                className="template-content-list-item"
                key={templateSection?.groupId || sectionIdx}
              >
                <Accordion className="accordion" defaultExpanded>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    className="header"
                  >
                    <div className="idx">
                      <p>{sectionIdx + 1}</p>
                    </div>
                    <p className="title">
                      {templateSection?.sectionTitle || "-"}
                    </p>
                  </AccordionSummary>
                  <AccordionDetails>
                    <div className="questions-list">
                      {Array.isArray(templateSection?.userControls) &&
                        templateSection?.userControls?.map(
                          (controls, indexInSection) => {
                            const answerKey = `a${globalIndex + 1}`;
                            const currentAnswerObject =
                              aiqaData?.[answerKey] || null;

                            const isAiqaEmpty = aiqaData?.length === 0;

                            const answerValue =
                              isAiqaEmpty && isSummaryGenerated
                                ? controls.value
                                : null;
                            const validAnswer = currentAnswerObject
                              ? Object.entries(currentAnswerObject).find(
                                  ([key]) => key.startsWith("a"),
                                )?.[1]
                              : answerValue;
                            let checkValidQuestion;
                            if (selectedPatientConsultation === null) {
                              checkValidQuestion = validAnswer !== null;
                            } else {
                              const valueLower = controls?.value?.toLowerCase();
                              checkValidQuestion =
                                !controls?.value ||
                                valueLower ===
                                  "not mentioned in the transcript" ||
                                valueLower === "not specified" ||
                                valueLower === "not mentioned" ||
                                valueLower === "not found" ||
                                valueLower === "not found in transcript" ||
                                controls?.value === null
                                  ? false
                                  : true;
                            }
                            const renderedItem =
                              checkValidQuestion && !isManualMode ? (
                                <>
                                  <div
                                    key={globalIndex}
                                    className="question-item answered"
                                  >
                                    <Accordion
                                      className="accordion"
                                      defaultExpanded
                                    >
                                      <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        className="title"
                                      >
                                        <p key={indexInSection}>
                                          {controls?.label || "-"}
                                        </p>
                                        <IoIosCheckmarkCircle
                                          color="#00cc6a"
                                          size={18}
                                        />
                                      </AccordionSummary>
                                      <AccordionDetails>
                                        <p key={indexInSection}>
                                          {controls?.value}
                                        </p>
                                      </AccordionDetails>
                                    </Accordion>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div
                                    key={globalIndex}
                                    className="question-item not-answered"
                                  >
                                    <p>{controls?.label || "-"}</p>
                                  </div>

                                  {/*Always show input if manual mode */}
                                  {isManualMode && (
                                    <div className="dropdown-item">
                                      <input
                                        type="text"
                                        className="pc-template-answers"
                                        placeholder="Enter your answer..."
                                        required={controls?.required}
                                        value={controls?.value || ""}
                                        onBlur={() => {
                                          setTimeout(() => {
                                            if (
                                              hydratedTemplateSections?.templateSections
                                            ) {
                                              updateConsultationTemplate({
                                                consultationId,
                                                payload:
                                                  hydratedTemplateSections.templateSections,
                                              });
                                            }
                                          }, 200);
                                        }}
                                        onChange={(e) =>
                                          handleAnswerChange(
                                            sectionIdx,
                                            indexInSection,
                                            e.target.value,
                                          )
                                        }
                                      />
                                    </div>
                                  )}
                                </>
                              );

                            globalIndex += 1;
                            return renderedItem;
                          },
                        )}
                    </div>
                  </AccordionDetails>
                </Accordion>
              </div>
            ))
          ) : isNhsSelected || isPathwaySavedState ? (
            <NHSServicesForms
              key={consultationId}
              isDarkMode={darkMode}
              selectedPatientConsultation={selectedPatientConsultation}
              consultationId={consultationId}
              showPreConsult={showPreConsult}
              showNHS111ReferralTab={showNHS111ReferralTab}
              onTemplateCancel={() => setIsNhsSelected(false)}
            />
          ) : (
            <div className="no-templates">
              <p>No Templates found</p>
              <p>Please select a template to continue...</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
